/**
 * Data fetching for the list screens.
 *
 * Deliberately small - a query library would be more than this app needs, since
 * every screen loads its own data from a server on the same machine and refetch
 * is a button rather than a background concern.
 *
 * What it does provide is the thing that actually bites: cancelling a stale
 * request. Type quickly in a search box and the responses come back out of
 * order; without the guard below, an older result overwrites a newer one and
 * the list shows the wrong rows.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';

/**
 * @param {Function} loader  async () => data. Must be stable (useCallback).
 * @param {Array} deps       re-runs when these change
 * @param {object} [options]
 * @param {boolean} [options.immediate=true]
 * @param {boolean} [options.toastError=true]
 */
export const useAsync = (loader, deps = [], { immediate = true, toastError = true } = {}) => {
    const [data, setData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(immediate);
    const [error, setError] = useState(null);

    // Only the newest call is allowed to write to state.
    const requestId = useRef(0);
    const mounted = useRef(true);

    /**
     * The flag has to be set on the way in, not just cleared on the way out.
     *
     * StrictMode mounts, unmounts and remounts every component in development.
     * A cleanup-only version sets `mounted` false on that simulated unmount and
     * never restores it, so every later response is discarded and the screen
     * sits on "Loading…" for ever. It works in production and hangs in
     * development, which is the worst way round.
     */
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const run = useCallback(async (...args) => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);

        try {
            const result = await loader(...args);
            if (!mounted.current || id !== requestId.current) return undefined;

            // The api layer returns { data, meta } for paged endpoints.
            if (result && typeof result === 'object' && 'meta' in result && 'data' in result) {
                setData(result.data);
                setMeta(result.meta);
            } else {
                setData(result);
                setMeta(null);
            }
            return result;
        } catch (err) {
            if (!mounted.current || id !== requestId.current) return undefined;
            setError(err);
            if (toastError) toast.error(err.message);
            return undefined;
        } finally {
            if (mounted.current && id === requestId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loader, toastError]);

    useEffect(() => {
        if (immediate) run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { data, meta, loading, error, refetch: run, setData };
};

/**
 * For a button that performs an action: tracks the in-flight state so the
 * button can disable itself, and surfaces success and failure as toasts.
 *
 *   const { run, busy } = useAction(orderApi.settle, { successMessage: 'Paid' });
 */
export const useAction = (action, { onSuccess, successMessage, toastError = true } = {}) => {
    const [busy, setBusy] = useState(false);

    const run = useCallback(async (...args) => {
        // Guard against a double tap on a touchscreen - two taps on Pay must
        // not settle a bill twice.
        if (busy) return undefined;

        setBusy(true);
        try {
            const result = await action(...args);
            if (successMessage) toast.success(successMessage);
            onSuccess?.(result);
            return result;
        } catch (err) {
            if (toastError) toast.error(err.message);
            throw err;
        } finally {
            setBusy(false);
        }
    }, [action, busy, onSuccess, successMessage, toastError]);

    return { run, busy };
};

export default useAsync;
